import { useMutation, useQuery } from "@apollo/client";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import CodeEditor from "components/CodeEditor";
import NoAccessPage from "components/error/NoAccessPage";
import FlashContext from "components/FlashMessages/FlashContext";
import PageLoading from "components/loading/PageLoading";
import ViewHeader from "components/ViewHeader";
import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import Callout from "ds/components/Callout";
import Select from "ds/components/Select";
import Typography from "ds/components/Typography";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { AuthorizationScheme, PolicyEngineType, PolicyType } from "types/generated";
import { AccountContext } from "views/AccountWrapper";

import { createPolicyTypesData } from "../Policies/helpers";
import { SpacesContext } from "../SpacesProvider";
import { showLoginPolicyCreateConfirmationModal } from "./ConfirmationModal";
import { LOGIN_POLICY_CREATE_ACTIVE_SESSIONS_WARNING_STORAGE_KEY } from "./constants";
import { CREATE_POLICY_V2, GET_ACCOUNT_AUTH_SCHEME } from "./gql";
import styles from "./styles.module.css";
import { useDetailsFromUrl } from "./useDetailsFromUrl";

const NewLoginPolicy = () => {
  const navigate = useNavigate();
  const { accountName, viewer } = useTypedContext(AccountContext);
  const { onError, reportSuccess } = useTypedContext(FlashContext);
  const { hasManageableSpaces } = useTypedContext(SpacesContext);
  const { name, labels } = useDetailsFromUrl();

  const { loading: authSchemeLoading, data } = useQuery<{
    authorizationScheme: AuthorizationScheme;
  }>(GET_ACCOUNT_AUTH_SCHEME);

  useTitle(`New login policy · ${accountName}`);

  const [body, setBody] = useState(
    createPolicyTypesData(PolicyEngineType.RegoV1)[PolicyType.Login]?.template || ""
  );
  const [engineType, setEngineType] = useState<PolicyEngineType>(PolicyEngineType.RegoV1);

  const handleEditorChange = (value?: string) => {
    setBody(value || "");
  };

  const [createPolicyV2, { loading }] = useMutation(CREATE_POLICY_V2, {
    variables: {
      input: {
        name: name || "",
        body,
        type: PolicyType.Login,
        labels: labels ?? [],
        engineType,
        description: null,
        space: null,
        policyTemplateULID: null,
      },
    },
    refetchQueries: ["GetLoginPolicies"],
    onError,
    onCompleted: (data) => {
      reportSuccess({
        // eslint-disable-next-line @cspell/spellchecker
        message: `Login policy ${data.policyCreatev2.name} successfully created`,
      });
      navigate(`/settings/login-policy`);
    },
  });

  const handleCreatePolicy = () => createPolicyV2();

  const handleConfirmation = () => {
    showLoginPolicyCreateConfirmationModal({
      onConfirm: handleCreatePolicy,
    });
  };

  useBreadcrumbs(
    [
      {
        title: "Organization settings",
      },
      {
        title: "Login Policy",
        link: "/settings/login-policy",
      },
      {
        title: name || "",
      },
    ],
    [name]
  );

  if (authSchemeLoading && !data?.authorizationScheme) {
    return <PageLoading />;
  }

  if (!viewer.admin && !hasManageableSpaces) {
    return <NoAccessPage />;
  }

  const isLoginPolicyActive = data?.authorizationScheme !== AuthorizationScheme.ManagedUsers;

  return (
    <>
      <ViewHeader backConfirmation="Your changes will not be saved.">
        <ViewHeaderWrapper direction="row" align="center" justify="between" fullWidth>
          <ViewHeaderTitle>{name}</ViewHeaderTitle>
        </ViewHeaderWrapper>
      </ViewHeader>

      <Box justify="stretch">
        <Box direction="column" className={styles.bodyWrapper}>
          <Box justify="between" align="center" className={styles.header}>
            <Typography tag="h3" variant="p-t5">
              Policy body
            </Typography>

            <Box gap="medium">
              <Select
                value={engineType}
                items={[
                  { label: "Rego V0", value: PolicyEngineType.RegoV0 },
                  { label: "Rego V1", value: PolicyEngineType.RegoV1 },
                ]}
                onChange={(value: PolicyEngineType) => setEngineType(value)}
              />
              <Button
                variant="primary"
                loading={loading}
                onPress={isLoginPolicyActive ? handleConfirmation : handleCreatePolicy}
              >
                Create
              </Button>
            </Box>
          </Box>
          {isLoginPolicyActive && (
            <Callout
              variant="danger"
              storageKey={LOGIN_POLICY_CREATE_ACTIVE_SESSIONS_WARNING_STORAGE_KEY}
            >
              After creating the Login policy, all active sessions (except the current one) will be
              invalidated.
            </Callout>
          )}

          <CodeEditor body={body} onChange={handleEditorChange} language="rego" />
        </Box>
      </Box>
    </>
  );
};

export default NewLoginPolicy;
